import { Clear as ClearIcon, WorkOff } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import ScrollShadow from "../../../../../design/scroll-shadow/scroll-shadow";
import { useDateFnsLocale } from "../../../../../utils/date-locale";
import useWorkerSchedulerData from "../../../controllers/use-worker-scheduler-data";
import {
  fetchWorkerFTShiftDetails,
  resetUpdateWorkerAvailabilityStatus,
} from "../../../store/worker-scheduler-slice";
import { getCapacityColor, getCapacityText } from "../../../utils";
import ReassignmentSectionSkeleton from "./reassignment-section-skeleton";

const sectionTitleStyle = {
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#000000DE",
};

const sectionValueStyle = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "16px",
	lineHeight: "24px",
	color: "#000000DE",
};

interface JobWorkcenter {
	jobId: number;
	workCenterId: number;
	jobName: string;
	workCenterName: string;
	jobWorkcenter: {
		jobId: number;
		workCenterId: number;
		jobName: string;
		workCenterName: string;
	}[];
}

interface ReassignmentSectionProps {
	workerFullName: string;
	shiftDetails: {
		shiftPatternName: string;
		endDateTime: string;
		startDateTime: string;
	};
	onConfirm: (selectedJobWC: { jobId: number; workCenterId: number }) => void;
	onCancel: () => void;
}

const ReassignmentSection = ({
	workerFullName,
	onConfirm,
	onCancel,
}: ReassignmentSectionProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	const {
		workerStatusTransitionDialog: {
			data: { shiftDetails, currentAssignment, shiftId },
		},
		updateAvailabilityStatusStatus,
		updateAvailabilityStatusErrorMessage,
	} = useWorkerSchedulerData();

	const [searchTerm, setSearchTerm] = useState("");

	const [selectedJobWC, setSelectedJobWC] = useState<{
		jobId: number;
		workCenterId: number;
	} | null>(null);

	const { fetchFTWorkerShiftDetailsStatus, fetchFTWorkerShiftDetails } =
		useWorkerSchedulerData();

	const handleJobSelect = (jobId: number, workCenterId: number) =>
		setSelectedJobWC({ jobId, workCenterId });

	const handleConfirm = () => selectedJobWC && onConfirm(selectedJobWC);

	useEffect(() => {
		if (!currentAssignment.jobWorkcenter.length || !shiftId) return;

		const uniqueWorkcenters = [
			...new Set(
				currentAssignment.jobWorkcenter.map(
					(job: JobWorkcenter) => job.workCenterId
				)
			),
		];

		const payload = uniqueWorkcenters.map((workcenterId) => ({
			shiftId: shiftId.toString(),
			workcenterId: Number(workcenterId),
		}));

		if (payload.length) {
			dispatch(fetchWorkerFTShiftDetails(payload));
		}
	}, [currentAssignment.jobWorkcenter, shiftId, dispatch]);

	const availableJobsAndWorkcenters = useMemo(() => {
		const jobs: any[] = [];

    console.log("fetchFTWorkerShiftDetails",fetchFTWorkerShiftDetails);
		fetchFTWorkerShiftDetails.forEach((item) => {
      const workcenterId = item.workcenterId;
			item.jobs.forEach((job) => {
				const jobsAndWc = currentAssignment.jobWorkcenter.filter(
					(item: JobWorkcenter) => item.jobId === job.jobId
				)?.filter((jobAndWc: { jobId: any; workCenterId: number;}) =>
          workcenterId === jobAndWc.workCenterId);
				if (jobsAndWc.length > 0) {
          jobsAndWc.forEach((jobAndWc: {
            jobId: number;
            workCenterId: number;
            jobName: string;
            workCenterName: string;
          }) => {
            if (
              (currentAssignment.jobId === jobAndWc.jobId &&
              currentAssignment.workcenterId !== jobAndWc.workCenterId)
              || (currentAssignment.jobId !== jobAndWc.jobId &&
              currentAssignment.workcenterId === jobAndWc.workCenterId)
              || (currentAssignment.jobId !== jobAndWc.jobId &&
              currentAssignment.workcenterId !== jobAndWc.workCenterId)
            ) {
              jobs.push({
                ...job,
                workCenterName: jobAndWc.workCenterName,
                jobName: jobAndWc.jobName,
                workCenterId: jobAndWc.workCenterId,
              });
            }
          })
				}
			});
		});

    // Remove duplicates based on jobId and workCenterId
    const uniqueJobs = new Map();
    return jobs.filter((job) => {
      const key = `${job.jobId}-${job.workCenterId}`;
      if (!uniqueJobs.has(key)) {
        uniqueJobs.set(key, true);
        return true;
      }
      return false;
    })?.sort((a, b) =>
      a.jobName.localeCompare(b.jobName))?.sort((a, b) => a.workCenterId - b.workCenterId);
	}, [
		fetchFTWorkerShiftDetails,
		currentAssignment.jobWorkcenter,
		currentAssignment.jobId,
    currentAssignment.workcenterId
	]);

	const filteredJobs = availableJobsAndWorkcenters.filter(
		(job) =>
			job.jobName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			job.workCenterName.toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (fetchFTWorkerShiftDetailsStatus === "pending") {
		return <ReassignmentSectionSkeleton />;
	}

	return (
		<>
			<Box>
				<Typography
					id='reassignment-dialog-title'
					sx={{
						fontFamily: "Roboto",
						fontWeight: 500,
						fontSize: "24px",
						lineHeight: "28px",
						color: "#000000DE",
						padding: "45px 45px 16px 45px",
					}}
				>
					{t("full-time-worker-scheduler_reassign-worker")} - {workerFullName}
				</Typography>
				<Box
					sx={{
						padding: "0 45px 20px 45px",
					}}
				>
					<Box sx={{ marginBottom: "16px" }}>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 400,
								fontSize: "14px",
								lineHeight: "20px",
								color: "#00000099",
							}}
							variant='caption'
						>
							{t("full-time-worker-scheduler_reassign-note")}
						</Typography>
					</Box>
					{/* Current Assignment Section */}
					<Box sx={{ marginBottom: "32px" }}>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "16px",
								lineHeight: "24px",
								color: "#000000DE",
								marginBottom: "16px",
							}}
						>
							{t("full-time-worker-scheduler_current-assignment")}
						</Typography>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								gap: "24px",
								marginBottom: "20px",
							}}
						>
							<Box sx={{ flex: 1 }}>
								<Typography sx={sectionTitleStyle}>
									{t("full-time-worker-scheduler_shift-details")}
								</Typography>
								<Typography sx={sectionValueStyle}>
									{shiftDetails.shiftPatternName},{" "}
									{format(
										new Date(shiftDetails.startDateTime),
										"EEE, d MMM h:mm a",
										{ locale: dateLocale }
									)}{" "}
									- {format(new Date(shiftDetails.endDateTime), "h:mm a")}
								</Typography>
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography sx={sectionTitleStyle}>
									{t("full-time-worker-scheduler_job-role-workcenter")}
								</Typography>
								<Typography sx={sectionValueStyle}>
									{currentAssignment.job} ({currentAssignment.workcenter})
								</Typography>
							</Box>
						</Box>
					</Box>

					{/* New Assignment Section */}
					<Box>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 500,
								fontSize: "16px",
								lineHeight: "24px",
								color: "#000000DE",
								marginBottom: "16px",
							}}
						>
							{t("full-time-worker-scheduler_reassign-worker-to")}
						</Typography>

						{availableJobsAndWorkcenters.length > 0 ? (
							<Box>
								{/* Search Field */}
								<TextField
									fullWidth
									placeholder={t("full-time-worker-scheduler_search-for-job-role")}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position='start'>
												<SearchIcon sx={{ color: "#00000061" }} />
											</InputAdornment>
										),
										endAdornment: searchTerm ? (
											<InputAdornment position='end'>
												<IconButton
													size='small'
													onClick={() => setSearchTerm("")}
													edge='end'
												>
													<ClearIcon sx={{ fontSize: "1.25rem" }} />
												</IconButton>
											</InputAdornment>
										) : null,
									}}
									sx={{
										marginBottom: "16px",
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#F5F5F5",
											border: "none",
											"& fieldset": {
												border: "none",
											},
											"&:hover fieldset": {
												border: "none",
											},
											"&.Mui-focused fieldset": {
												border: "none",
											},
										},
									}}
								/>

                <ScrollShadow height={215}>
                  {/* Job Roles List */}
                  <List>
                    {filteredJobs?.map((job: any) => (
                      <ListItem
                        key={`${job.jobId}-${job.workCenterId}`}
                        disablePadding
                        sx={{ marginBottom: "8px" }}
                      >
                        <ListItemButton
                          selected={selectedJobWC?.jobId === job.jobId && selectedJobWC?.workCenterId === job.workCenterId}
                          onClick={() =>
                            handleJobSelect(job.jobId, job.workCenterId)
                          }
                          sx={{
                            border: "1px solid #E0E0E0",
                            borderRadius: "4px",
                            padding: "12px 16px",
                            "&.Mui-selected": {
                              backgroundColor: "#E3F2FD",
                              border: "1px solid #1976D2",
                            },
                            "&:hover": {
                              backgroundColor: "#F5F5F5",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 400,
                                  fontSize: "16px",
                                  lineHeight: "24px",
                                  color: "#000000DE",
                                }}
                              >
                                {job.jobName} - {job.workCenterName}
                              </Typography>
                            }
                          />
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: "12px",
                              fontStyle: "normal",
                              lineHeight: "19.92px",
                              color: getCapacityColor(job.status),
                              marginLeft: "auto",
                              letterSpacing: "0.4px",
                            }}
                          >
                            {getCapacityText(job)}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </ScrollShadow>
							</Box>
						) : (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									gap: "8px",
									marginTop: "32px",
									flexDirection: "column",
                  minHeight: "198px",
                  maxHeight: "198px",
								}}
							>
								<WorkOff sx={{ fontSize: "24px", color: "#00000061" }} />
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "16px",
										lineHeight: "24px",
										color: "#00000061",
									}}
								>
									{t("full-time-worker-scheduler_no-job-roles-available")}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
				<Box
					sx={{
						padding: "8px 45px 45px 45px",
						gap: "8px",
						display: "flex",
						justifyContent: "flex-end",
					}}
				>
					<Button
						onClick={onCancel}
						variant='text'
						sx={{
							fontFamily: "Roboto",
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "20px",
							letterSpacing: "0.1px",
							textTransform: "uppercase",
							minWidth: "64px",
							padding: "8px 16px",
						}}
					>
						{t("common_cancel")}
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={
							!selectedJobWC || availableJobsAndWorkcenters.length === 0
						}
						variant='contained'
						sx={{
							fontFamily: "Roboto",
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "20px",
							letterSpacing: "0.1px",
							textTransform: "uppercase",
							minWidth: "120px",
							padding: "8px 16px",
						}}
					>
						{t("full-time-worker-scheduler_confirm-move")}
					</Button>
				</Box>{" "}
			</Box>
			{updateAvailabilityStatusStatus === "error" && (
				<Snackbar
					open={updateAvailabilityStatusStatus === "error"}
					autoHideDuration={6000}
					onClose={() => dispatch(resetUpdateWorkerAvailabilityStatus())}
					anchorOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<Alert elevation={6} variant='filled' severity='error'>
						{updateAvailabilityStatusErrorMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	);
};

export default ReassignmentSection;
